/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUserShareSettings } from "../../../Actions/Actions";
import {
    getResults,
    loadGPSInsightsSuccess,
    setLoadingGpsMobileInsightsReport,
} from "../../../Actions/GPSInsightsActions";
import { setIsMapReady } from "../../../Actions/ReportMapActions";
import { getCategories } from "../../../Actions/RetailMatchActions";
import { featureFlags } from "../../../Common/CurrentFeatureFlags";
import { DigitalShareAppType } from "../../../Enums/DigitalShareAppType";
import { RenderType } from "../../../Enums/RenderType";
import { IDigitalShareItem } from "../../../Models/IDigitalShareItem";
import { IUserShareSettings } from "../../../Models/IUserShareSettings";
import { IGPSGetJobResultParsed, IUser } from "../../../Models/Models";
import {
    getGpsReportDataSelector,
    gpsConditionalOutputsSelector,
    gpsLoadingSaveToScoutSelector,
    loadingMobilyticsReportSelector,
} from "../../../Reducers/Selectors/GPSInsightsSelectors";
import { userShareSettingsSelector } from "../../../Reducers/Selectors/UserSelectors";
import {
    filterGpsOutputsAgainstAvailableReports,
    gpsDigitalShareOutputs,
    GPSInsightsTemplateDisclaimer,
} from "../../../Util/ScoutDigitalShareUtil";
import { topNavHeightPx } from "../../../View/Products/Constants/StyleConstants";
import {
    ComponentLoading,
    getMosaicSegments,
    PlaceholderCard,
    ReportOuterWrapper,
} from "../../Common/";
import { RenderContainer } from "../../Common/DigitalShare/DigitalShareCommonStyles";
import {
    DigitalShareGenerator,
    PrintGenerator,
} from "../../Common/DigitalShare/DigitalShareGenerator";
import "../../Layout/Body.css";
import GPSInsightsDigitalShareTemplate from "./DigitalShare/Templates/GPSInsightsDigitalShareTemplate";
import GPSHeader from "./GPSHeader/GPSHeader";
import { GPSInsightsForm } from "./GPSInsightsForm";
import { GPSInsightsReport } from "./GPSInsightsReport";
import GPSStickyNav from "./GPSStickyNav";

interface IGPSInsightsContainerProps {
    isSharing?: boolean;
    pathname: string;
    jobId: string;
    user: IUser;
    isScout: boolean;
}

const GPSInsightsReportContainer: FunctionComponent<IGPSInsightsContainerProps>
    = (props: IGPSInsightsContainerProps) => {
        /** misc */

        // We're using props here since they are memoized.
        // useLocation() and useParams() pull from the global location data and will
        // force the component to re-render every time, even if they haven't changed.
        const { isSharing, pathname, jobId } = props;
        const dispatch = useDispatch();

        const gpsInsightsReportContainerRoutePathMatch = new RegExp(/job_./);
        const loadingSaveToScout: boolean = useSelector(gpsLoadingSaveToScoutSelector);
        //const user: IUser = useSelector(gpsUserSelector);
        const user = props.user;
        const userShareSettings: IUserShareSettings = useSelector(userShareSettingsSelector);
        const loadingMobilyticsReport = useSelector(loadingMobilyticsReportSelector);

        // Booleans used for determining what outputs to hide in DS share dialogue
        const { hasFvpData, hasVisitorLoyaltyData, hasTradeAreaAnalysis } = useSelector(gpsConditionalOutputsSelector);

        const {
            currentJobData,
            polygons,
            isGpsInsightModalVisible,
            selectedInsight,
            benchmarkCommonData,
            currentBenchmarkPolygon,
            benchmarkItems,
        } = useSelector(getGpsReportDataSelector);

        useEffect(() => {
            dispatch(setIsMapReady(false));
            if (!userShareSettings) {
                dispatch(getUserShareSettings());
            }
            dispatch(getMosaicSegments());
            dispatch(getCategories());
            // Reset the GPS Insights data on unmount -- otherwise, data will still be left
            // and it will try to render the full component next time a report is visited, and
            // cause errors due to other state being uninitialized.
            return () => {
                const emptyData: IGPSGetJobResultParsed = {
                    polygon: [],
                    data: [],
                    job: [],
                    benchmarkCommonData: null,
                };
                dispatch(loadGPSInsightsSuccess(emptyData));
            };
        }, []);

        useEffect(() => {
            if (!props.isSharing) {
                dispatch(setLoadingGpsMobileInsightsReport(true));
                dispatch(getResults(jobId));
            }
        }, [jobId]);

        const [isPrinting, setIsPrinting] = useState(false);
        const [renderType, setRenderType] = useState(RenderType.DEFAULT);
        const [isChoosingDigitalShare, setIsChoosingDigitalShare]
            = useState<boolean>(false);
        const [isDebuggingTemplate, setIsDebuggingTemplate] = useState(false);

        const onChangeGenerating = (isGenerating: boolean) => {
            setRenderType(
                isGenerating ? RenderType.DIGITAL_SHARE : RenderType.DEFAULT
            );
        };

        const handleIsSharing = (): boolean => {
            const pathnameMatch = pathname.match(gpsInsightsReportContainerRoutePathMatch);
            const share = isSharing
                ? true
                : pathnameMatch !== null && user !== null;

            return share;
        };

        const onPrintClick = () => {
            setIsPrinting(true);
        };

        let digitalShareOutputs: IDigitalShareItem[] = gpsDigitalShareOutputs(
            polygons?.length < 2
        );

        // Filter outputs against available report data
        digitalShareOutputs = filterGpsOutputsAgainstAvailableReports(
            digitalShareOutputs,
            hasVisitorLoyaltyData,
            hasFvpData,
            hasTradeAreaAnalysis
        );

        (window as any).showTemplate = () => {
            setIsDebuggingTemplate(true);
        };
        (window as any).hideTemplate = () => {
            setIsDebuggingTemplate(false);
        };

        return (
            <>
                {
                    handleIsSharing()
                    // Handle both null and empty array
                        ? currentJobData
                        && currentJobData.length > 0
                        && polygons
                        && selectedInsight.length > 0
                        && !loadingMobilyticsReport
                            ? <>
                                {isGpsInsightModalVisible && <GPSInsightsForm />}
                                {isDebuggingTemplate && (
                                    <RenderContainer style={{ backgroundColor: "#eee" }}>
                                        <GPSInsightsDigitalShareTemplate
                                            polygonComparisons={[]}
                                            captionAboveTitle={"01"}
                                            sectionTitle={"Trade Area"}
                                            disclaimer={GPSInsightsTemplateDisclaimer}
                                            itemType={"Item Title"}
                                            hideItemText={true}
                                            hideItemTypeTitle={true}
                                        >
                                            <></>
                                        </GPSInsightsDigitalShareTemplate>
                                    </RenderContainer>
                                )}
                                <ReportOuterWrapper
                                    id="GPSReportBody"
                                    style={{
                                        top: 0,
                                        paddingTop: props.isSharing || !props.isScout
                                            ? 0
                                            : topNavHeightPx
                                    }}

                                    // Prevents #12584 (Google Map receiving focus and causing page to jump)
                                    tabIndex={0}
                                >
                                    <GPSHeader
                                        job={selectedInsight}
                                        isSharing={isSharing}
                                        loadingSaveToScout={loadingSaveToScout}
                                        onPrintClick={onPrintClick}
                                        onShareClick={() => setIsChoosingDigitalShare(true)}
                                    />
                                    <GPSStickyNav isSharing={isSharing} isScout={props.isScout} />
                                    <GPSInsightsReport
                                        storeData={currentJobData}
                                        polygons={polygons}
                                        benchmarkData={
                                            currentJobData.length > 1 ? currentJobData[1] : null
                                        }
                                        benchmarkCommonData={benchmarkCommonData}
                                        selectedGpsInsightForReportView={selectedInsight}
                                        benchmarkItems={benchmarkItems}
                                        currentBenchmarkPolygon={currentBenchmarkPolygon}
                                        renderType={renderType}
                                    />
                                </ReportOuterWrapper>

                                {featureFlags.DigitalShare && isChoosingDigitalShare && (
                                    <DigitalShareGenerator
                                        onClose={() => setIsChoosingDigitalShare(false)}
                                        onChangeGenerating={onChangeGenerating}
                                        app={DigitalShareAppType.Mobilytics}
                                        isChoosingDigitalShare={isChoosingDigitalShare}
                                        outputs={digitalShareOutputs}
                                    />
                                )}

                                {featureFlags.DigitalShare && isPrinting && (
                                    <PrintGenerator
                                        onComplete={() => setIsPrinting(false)}
                                        onChangeGenerating={onChangeGenerating}
                                        app={DigitalShareAppType.Mobilytics}
                                        outputs={digitalShareOutputs}
                                        jobId={selectedInsight[0].jobId}
                                    />
                                )}
                            </>
                            : <ComponentLoading title="Loading Insight" />

                        : <PlaceholderCard
                            iconType="warning"
                            text="You do not have permission to view this page"
                        />
                }
            </>
        );
    };

export default React.memo(GPSInsightsReportContainer);
